import { Dispatch, useEffect, useMemo, useReducer } from 'react'
import { STEP, Step } from '@lifedot/constants/dialogue'
import { sects } from '@lifedot/constants/searchChoices'
import { dialogueAnswers, QUESTION_STEP } from '@lifedot/constants/dialogue'

type Answers = Record<Step, string | null>
type Status = 'WAITING' | 'NOT_ENOUGH' | 'ANSWERED'

type UseDiscoverDialogue = (
  currentStep: Step
) => [{ answers: Answers; status: Status }, { answer: Dispatch<string> }]

export const useDiscoverDialogue: UseDiscoverDialogue = (currentStep) => {
  const [answers, answer] = useReducer((status: Answers, action: string) => {
    return { ...status, [currentStep]: action }
  }, initLocalStorage())

  useEffect(() => {
    window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(answers))
  }, [answers])

  const status = useMemo<Status>(() => {
    const prevStep = getPrevStep(currentStep)
    const prevSteps = getPrevSteps(currentStep)
    const isCorrect = prevSteps?.every((step) => isCorrectAnswer(step, answers))

    if ((prevStep && !answers[prevStep]) || !isCorrect) return 'NOT_ENOUGH'
    if (answers[currentStep]) return 'ANSWERED'
    return 'WAITING'
  }, [answers, currentStep])

  return [{ answers, status }, { answer }]
}

const LOCALSTORAGE_KEY = 'discover-dialogue'

const initials: Answers = {
  step1: null,
  step2: null,
  step3: null,
  step4: null,
  result: null
}

const initLocalStorage = (): Answers => {
  if (typeof window === 'undefined') return initials
  const answers: Partial<Answers> = JSON.parse(
    window.localStorage.getItem(LOCALSTORAGE_KEY) ?? '{}'
  )

  return { ...initials, ...answers }
}

const steps: Step[] = [
  STEP.FIRST,
  STEP.SECOND,
  STEP.THIRD,
  STEP.FOURTH,
  STEP.RESULT
]

const getPrevStep = (current: Step): Step | null => {
  return steps[steps.findIndex((i) => i === current) - 1] ?? null
}

const getPrevSteps = (current: Step): Step[] | null => {
  const index = steps.findIndex((i) => i === current)
  return steps.slice(0, index)
}

const sectionTypeAnswers = Object.values(dialogueAnswers.sectionType)
const priceAnswers = Object.values(dialogueAnswers.price)
const sectAnswers = [...sects.map((v) => v.code), dialogueAnswers.sect.nothing]

const isCorrectAnswer = (step: Step, answers: Answers): boolean => {
  if (step === QUESTION_STEP.AREA)
    return (answers[QUESTION_STEP.AREA] ?? '').includes('geo')
  if (step === QUESTION_STEP.SECTION_TYPE)
    return sectionTypeAnswers.includes(
      answers[QUESTION_STEP.SECTION_TYPE] ?? ''
    )
  if (step === QUESTION_STEP.PRICE)
    return priceAnswers.includes(answers[QUESTION_STEP.PRICE] ?? '')
  if (step === QUESTION_STEP.SECT)
    return sectAnswers.includes(answers[QUESTION_STEP.SECT] ?? '')
  return true
}
